import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Switch,
  Typography,
} from "@mui/material";
import { Logout as LogoutIcon } from "@mui/icons-material";
import GitHubTokenSection from "../components/GitHubTokenSection";
import { useAuth } from "../hooks/useAuth";
import { useThemeMode } from "../hooks/useThemeMode";
import { useUISettings } from "../hooks/useUISettings";

const Settings: React.FC = () => {
  const { githubConnected, user, logout, loading } = useAuth();
  const { mode, setMode } = useThemeMode();
  const { autoHideNavigation, setAutoHideNavigation } = useUISettings();

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
        Settings
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Manage platform access and connected services.
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Account
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Signed in as {user?.email || "Unknown user"}
          </Typography>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={
              loading ? <CircularProgress size={16} color="inherit" /> : <LogoutIcon />
            }
            onClick={logout}
            disabled={loading}
          >
            Sign Out
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Appearance
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="body1">Dark mode</Typography>
            <Typography variant="body2" color="text.secondary">
              Use a darker color palette across the entire app.
            </Typography>
          </Box>
          <Switch
            checked={mode === "dark"}
            onChange={(event) =>
              setMode(event.target.checked ? "dark" : "light")
            }
            inputProps={{ "aria-label": "Toggle dark mode" }}
          />
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Navigation
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="body1">Auto-hide side navigation</Typography>
            <Typography variant="body2" color="text.secondary">
              Keep the side navigation collapsed by default, expand it on hover,
              and collapse it again when the pointer leaves.
            </Typography>
          </Box>
          <Switch
            checked={autoHideNavigation}
            onChange={(event) => setAutoHideNavigation(event.target.checked)}
            inputProps={{ "aria-label": "Toggle auto-hide side navigation" }}
          />
        </Box>
      </Paper>

      <GitHubTokenSection />

      {!githubConnected && (
        <Alert severity="info">
          Connect GitHub access here before using repository-backed translation
          workflows.
        </Alert>
      )}
    </Box>
  );
};

export default Settings;
